import * as R from 'ramda';
import React, { useContext, useEffect, useState } from 'react';
import { BrowserView, isMobileOnly, MobileOnlyView, TabletView } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Container from '../common/components/Container/Container';
import OwnerSignsMessage from '../common/components/OwnerSignsMessage';
import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import SVGWalletApp from '../common/assets/svg/icons/SVGWalletApp';
import { Routes } from '../config/routes';
import { FlowConfigContext } from '../providers/FlowConfigContext';
import { ScopeRequestContext } from '../providers/ScopeRequestContext';
import { QrCodeTheme, ScopeRequestStatus } from '../services/secureIdentityProvider';
import { GatekeeperNetworkServiceContext } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { Screens } from '../services/analytics/screens';

function QRCodeScreen(): JSX.Element {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState<string>('');
  const { createScopeRequest, fetchQRCode, qrCodeOrDeepLinkFetched, fetchDeepLink, scopeRequest, awaitingUser } =
    useContext(ScopeRequestContext);
  const { initialized: flowConfigInitialized } = useContext(FlowConfigContext);
  const { requestComplete: gknServiceRequestComplete } = useContext(GatekeeperNetworkServiceContext);

  const navigate = useNavigate();

  useEffect(() => {
    switch (scopeRequest?.status) {
      case ScopeRequestStatus.USER_CANCELLED:
        navigate(Routes.CANCEL);
        break;
      case ScopeRequestStatus.VERIFICATION_FAILED:
        navigate(Routes.ERROR);
        break;
      default:
        break;
    }
  }, [scopeRequest?.status]);

  useEffect(() => {
    const abortController = new AbortController();
    // We can only request an SR once flow config is initialized and the GK Network Service has responded.
    // Also don't request one if we already have one.
    if (!flowConfigInitialized || !gknServiceRequestComplete || scopeRequest) {
      return;
    }

    async function requestNewScopeRequest() {
      const result = await createScopeRequest();
      if (abortController.signal.aborted) {
        return;
      }
      result.logError('QrCodeScreen: Error fetching new scope request');
      if (result.isFailed()) {
        navigate(-1);
      }
    }

    requestNewScopeRequest();
    return () => abortController.abort();
  }, [flowConfigInitialized, gknServiceRequestComplete]);

  useEffect(() => {
    const abortController = new AbortController();
    // do not fetch QR code if there is no scope request, or if we have already fetched the QR code / deep link
    if (!scopeRequest || qrCodeOrDeepLinkFetched) {
      return;
    }

    async function loadQRCode() {
      if (!flowConfigInitialized) {
        return;
      }

      // On mobile only we need to fetch and open the deep link instead of showing the QR
      if (isMobileOnly) {
        const response = await fetchDeepLink();
        if (abortController.signal.aborted) {
          return;
        }
        response.logError('QrCodeScreen: Error fetching mobile-only deep link');
        if (response.isFailed()) {
          navigate(-1);
        } else {
          window.open(response.success(), '_blank');
        }
      } else {
        const response = await fetchQRCode(QrCodeTheme.DEFAULT);
        if (abortController.signal.aborted) {
          return;
        }
        response.logError('QrCodeScreen: Error fetching QR code');
        if (response.isFailed()) {
          navigate(-1);
          return;
        }

        setQrCode(response.success());
      }
    }
    loadQRCode();
    return () => abortController.abort();
  }, [flowConfigInitialized, scopeRequest]);

  const renderLoadingDots = () => {
    return (
      <>
        <div className="loading-dots-wrap">
          <div className="pass-dot-pulse" />
        </div>
      </>
    );
  };

  const renderQRCode = () => {
    if (!R.isEmpty(qrCode)) {
      return <img data-testid="IMAGE_QRCODE" src={qrCode} alt={t('screens.captchaPass.qrcode.imageLabel')} />;
    }
    return renderLoadingDots();
  };

  const renderQrCodePanel = () => {
    return (
      <>
        <TitleContainer center>
          <h1>{t('screens.captchaPass.qrcode.title')}</h1>
        </TitleContainer>

        <div className="sr-qrcode flex justify-center items-center">{renderQRCode()}</div>
      </>
    );
  };

  return (
    <Container>
      <>
        {!awaitingUser && (
          <>
            <div className="flex justify-center items-center polling-message flex-col my-4">
              <SVGWalletApp />
              <div className="flex flex-col justify-center items-center mt-4 pt-2">
                <h2 className="text-xl font-bold leading-5 text-textTertiary" data-testid="USER_ACKNOWLEDGED_HEADER_1">
                  {t('screens.captchaPass.qrcode.continue.titleLine1')}
                </h2>
                <h2
                  className="text-xl font-bold leading-4 text-textTertiary mt-2"
                  data-testid="USER_ACKNOWLEDGED_HEADER_2"
                >
                  {t('screens.captchaPass.qrcode.continue.titleLine2')}
                </h2>
                <span
                  className="inline-block text-xs text-center leading-4 text-textParagraph my-4"
                  data-testid="USER_ACKNOWLEDGED_TEXT"
                >
                  {t('screens.captchaPass.qrcode.continue.message')}
                </span>
              </div>
            </div>
            <OwnerSignsMessage marginBottom={0} />
          </>
        )}
        {awaitingUser && (
          <>
            <BrowserView className="qr-content-wrap">{renderQrCodePanel()}</BrowserView>
            <TabletView className="qr-content-wrap">{renderQrCodePanel()}</TabletView>
            <MobileOnlyView className="qr-content-wrap">{renderLoadingDots()}</MobileOnlyView>
          </>
        )}
      </>
    </Container>
  );
}
export default withRouteAnalytics(QRCodeScreen, Screens.CIVIC_APP_QR);
